import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Loader2, Mail, Send, Smartphone, Monitor, AlertTriangle } from 'lucide-react';

export const ACEmailPreviewDialog = ({ open, onOpenChange, onSent }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [mobile, setMobile] = useState(true);

  useEffect(() => {
    if (!open) return;
    setData(null); setError('');
    api.getAcEmailPreview().then(r => setData(r.data))
      .catch(err => setError(err.response?.data?.detail || 'Vorschau konnte nicht erstellt werden'));
  }, [open]);

  const send = async () => {
    if (!window.confirm('AC-Agenda-E-Mail jetzt wirklich versenden? Die enthaltenen Deals werden danach automatisch von der AC-Vormerkung genommen.')) return;
    setSending(true);
    try {
      const res = await api.sendAcEmailNow();
      toast.success(`AC-Agenda an ${res.data.sent_to?.length || 0} Empfänger versendet`);
      onOpenChange(false);
      onSent?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Senden fehlgeschlagen'); }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-4xl h-[88vh] flex flex-col" data-testid="ac-preview-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
            <Mail className="w-5 h-5 text-purple-400" /> AC-E-Mail — Vorschau
          </DialogTitle>
        </DialogHeader>
        {error ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-sm text-amber-400 flex items-center gap-2" data-testid="ac-preview-error">
              <AlertTriangle className="w-4 h-4" /> {error}
            </p>
          </div>
        ) : !data ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 flex-wrap text-[11px] text-muted-foreground">
              <span className="text-foreground font-medium" data-testid="ac-preview-subject">{data.subject}</span>
              <span>· {data.deal_count} Deals</span>
              <span>· an {data.recipients.length} Empfänger{data.recipients.length !== 1 ? '' : ''}</span>
              <div className="ml-auto flex items-center gap-1">
                <button onClick={() => setMobile(true)} data-testid="ac-preview-mobile-btn"
                  className={`h-7 px-2 rounded-sm border flex items-center gap-1 text-[11px] ${mobile ? 'border-purple-500/50 text-purple-300 bg-purple-500/10' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                  <Smartphone className="w-3.5 h-3.5" /> Mobil
                </button>
                <button onClick={() => setMobile(false)} data-testid="ac-preview-desktop-btn"
                  className={`h-7 px-2 rounded-sm border flex items-center gap-1 text-[11px] ${!mobile ? 'border-purple-500/50 text-purple-300 bg-purple-500/10' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                  <Monitor className="w-3.5 h-3.5" /> Desktop
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden bg-[#e5e7eb] rounded-sm flex justify-center py-3">
              <iframe title="AC-E-Mail-Vorschau" srcDoc={`<body style="margin:0; padding:12px; background:#ffffff;">${data.html}</body>`}
                style={{ width: mobile ? 390 : 720, maxWidth: '100%', height: '100%', border: 'none', background: '#fff', borderRadius: 4 }}
                data-testid="ac-preview-iframe" />
            </div>
            <div className="flex items-center justify-between gap-2 pt-1 flex-wrap">
              <p className="text-[10px] text-muted-foreground">
                So kommt die E-Mail bei den AC-Mitgliedern an. Der echte Versand entfernt die '!'-Vormerkung der enthaltenen Deals.
              </p>
              <div className="flex items-center gap-2 ml-auto">
                <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-xs h-8 px-3">Schließen</Button>
                <Button onClick={send} disabled={sending || !data.sender_ready || !data.recipients.length}
                  data-testid="ac-preview-send-btn"
                  title={!data.sender_ready ? 'Kein Absender-Konto konfiguriert (AC Settings)' : (!data.recipients.length ? 'Keine Empfänger konfiguriert (AC Settings)' : '')}
                  className="bg-purple-600 hover:bg-purple-500 text-white rounded-sm text-xs h-8 px-3">
                  {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                  Jetzt senden
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
